import React from 'react';
import { ArrowRight, CheckCircle, Code, Zap, Shield, Users, Palette, Search, ShoppingCart } from 'lucide-react';
import Layout from '../../layouts/Layout';
import CTA from '../../components/CTA';
import { Link } from 'react-router-dom';

const WordPressDevelopmentPage = () => {
    const services = [
        {
            icon: <Palette className="w-6 h-6" />,
            title: "Custom Theme Development",
            description: "Bespoke WordPress themes designed and built specifically for your brand and requirements.",
            features: ["Responsive design", "Custom post types", "Advanced Custom Fields", "Performance optimised"]
        },
        {
            icon: <Code className="w-6 h-6" />,
            title: "Plugin Development",
            description: "Custom WordPress plugins to extend functionality and integrate with third-party systems.",
            features: ["Custom functionality", "API integrations", "Database optimisation", "Security hardened"]
        },
        {
            icon: <ShoppingCart className="w-6 h-6" />,
            title: "WooCommerce Solutions",
            description: "Complete e-commerce solutions with custom checkout flows and payment integrations.",
            features: ["Custom product types", "Payment gateway integration", "Inventory management", "Performance tuning"]
        },
        {
            icon: <Search className="w-6 h-6" />,
            title: "SEO Optimisation",
            description: "WordPress sites optimised for search engines with technical SEO best practices.",
            features: ["Schema markup", "Page speed optimisation", "XML sitemaps", "Meta tag management"]
        }
    ];

    const features = [
        {
            icon: <Zap className="w-6 h-6" />,
            title: "Lightning Fast Performance",
            description: "Optimised WordPress sites with advanced caching, CDN integration, and database tuning for exceptional speed."
        },
        {
            icon: <Shield className="w-6 h-6" />,
            title: "Enterprise Security",
            description: "Hardened WordPress installations with security plugins, regular updates, and vulnerability monitoring."
        },
        {
            icon: <Users className="w-6 h-6" />,
            title: "User-Friendly CMS",
            description: "Intuitive admin interfaces that make content management simple for non-technical users."
        },
        {
            icon: <Code className="w-6 h-6" />,
            title: "Clean, Scalable Code",
            description: "Well-structured, documented code following WordPress coding standards for easy maintenance and updates."
        }
    ];

    const solutions = [
        {
            title: "Business Websites",
            description: "Professional corporate websites with custom designs and functionality",
            features: [
                "Custom theme development",
                "Content management system",
                "Contact forms and lead capture", 
                "SEO optimisation",
                "Social media integration",
                "Analytics integration"
            ]
        },
        {
            title: "E-commerce Stores",
            description: "Complete WooCommerce solutions for online retail businesses",
            features: [
                "Product catalogue management",
                "Shopping cart and checkout",
                "Payment gateway integration",
                "Inventory management",
                "Order tracking system",
                "Customer accounts"
            ]
        },
        {
            title: "Membership Sites",
            description: "Subscription-based websites with member-only content and features",
            features: [
                "User registration and login",
                "Subscription management",
                "Protected content areas",
                "Payment processing", 
                "Member directories",
                "Community features"
            ]
        },
        {
            title: "Multi-site Networks",
            description: "WordPress multisite installations for managing multiple websites",
            features: [
                "Centralised management",
                "Shared user databases",
                "Custom themes and plugins",
                "Network administration",
                "Site provisioning",
                "Unified branding"
            ]
        }
    ];

    const technologies = [
        {
            category: "WordPress Core",
            items: ["WordPress 6.0+", "Gutenberg Blocks", "Custom Post Types", "Advanced Custom Fields", "WordPress REST API", "Multisite Networks"]
        },
        {
            category: "Frontend Technologies",
            items: ["PHP 8.0+", "HTML5 & CSS3", "JavaScript ES6+", "SASS/SCSS", "Bootstrap/Tailwind", "jQuery"]
        },
        {
            category: "E-commerce & CMS",
            items: ["WooCommerce", "Easy Digital Downloads", "LearnDash", "BuddyPress", "bbPress", "Elementor Pro"]
        },
        {
            category: "Performance & SEO",
            items: ["WP Rocket", "Cloudflare", "Yoast SEO", "Schema Pro", "Google Analytics", "Search Console"]
        }
    ];

    const process = [
        {
            step: "01",
            title: "Requirements Analysis",
            description: "We analyse your business needs, target audience, and technical requirements to create a comprehensive WordPress strategy."
        },
        {
            step: "02",
            title: "Design & Planning", 
            description: "Our team creates custom designs and technical architecture plans that align with your brand and business objectives."
        },
        {
            step: "03",
            title: "Development & Testing",
            description: "We build your WordPress site with custom themes and plugins, testing extensively across devices and browsers."
        },
        {
            step: "04",
            title: "SEO & Performance Optimisation",
            description: "We optimise your site for search engines and performance, ensuring fast loading times and excellent user experience."
        },
        {
            step: "05",
            title: "Launch & Training",
            description: "We handle the launch process and provide comprehensive training so you can manage your WordPress site effectively."
        }
    ];

    const benefits = [
        "Custom WordPress themes designed specifically for your brand",
        "Plugin development for unique functionality requirements",
        "WooCommerce e-commerce solutions with payment integration",
        "SEO optimisation for improved search engine visibility",
        "Performance tuning for fast loading times",
        "Security hardening and regular updates",
        "Content management training for your team",
        "Ongoing maintenance and support packages"
    ];

    const industries = [
        "Professional Services",
        "E-commerce & Retail",
        "Healthcare & Medical",
        "Education & Training",
        "Non-profit Organisations",
        "Real Estate",
        "Hospitality & Tourism",
        "Manufacturing"
    ];

    return (
        <Layout
            title="WordPress Development Services"
            description="Professional WordPress development services in the UK. Custom themes, plugins, WooCommerce solutions, and maintenance. Expert WordPress developers with 10+ years experience."
            keywords="WordPress development UK, custom WordPress themes, WooCommerce development, WordPress plugins, WordPress maintenance, WordPress SEO"
            path="/services/wordpress-development"
        >
            <div className="min-h-screen bg-white">
                {/* Hero Section */}
                <section className="relative pt-32 pb-20 px-6 bg-gradient-to-br from-slate-50 to-slate-100">
                    <div className="max-w-6xl mx-auto">
                        <div className="grid lg:grid-cols-2 gap-12 items-center">
                            <div>
                                <div className="inline-flex items-center px-4 py-2 bg-slate-900 text-white rounded-full text-sm font-medium mb-8">
                                    <Code className="w-4 h-4 mr-2" />
                                    WordPress Development
                                </div>
                                
                                <h1 className="text-5xl lg:text-6xl font-bold text-slate-900 mb-8 leading-tight">
                                    Professional WordPress
                                    <span className="block text-slate-700 mt-2">Solutions</span>
                                </h1>
                                
                                <p className="text-xl text-slate-600 mb-10 leading-relaxed">
                                    Transform your online presence with custom WordPress development. We create bespoke themes, 
                                    powerful plugins, and WooCommerce solutions that drive business growth whilst providing 
                                    exceptional user experiences.
                                </p>
                                
                                <div className="flex flex-col sm:flex-row gap-4">
                                    <Link to="/contact" className="bg-slate-900 text-white px-8 py-4 rounded-xl font-semibold hover:bg-slate-800 transition-all hover:shadow-lg flex items-center justify-center">
                                        Start Your WordPress Project <ArrowRight className="ml-2 w-5 h-5" />
                                    </Link>
                                    <Link to="/portfolio" className="border-2 border-slate-200 text-slate-700 px-8 py-4 rounded-xl font-semibold hover:border-slate-300 hover:bg-slate-50 transition-all">
                                        View Our Portfolio
                                    </Link>
                                </div>
                            </div>
                            
                            <div className="relative">
                                <div className="bg-white rounded-3xl p-8 shadow-2xl border border-slate-200">
                                    <div className="space-y-6">
                                        <div className="flex items-center justify-between">
                                            <span className="text-slate-600">Page Speed Score</span>
                                            <span className="text-slate-900 font-semibold">95+/100</span>
                                        </div>
                                        <div className="flex items-center justify-between">
                                            <span className="text-slate-600">SEO Optimisation</span>
                                            <span className="text-slate-900 font-semibold">A+</span>
                                        </div>
                                        <div className="flex items-center justify-between">
                                            <span className="text-slate-600">Security Rating</span>
                                            <span className="text-slate-900 font-semibold">Excellent</span>
                                        </div>
                                        <div className="flex items-center justify-between">
                                            <span className="text-slate-600">Uptime Guarantee</span>
                                            <span className="text-slate-900 font-semibold">99.9%</span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>

                {/* Services Section */}
                <section className="py-24 bg-white">
                    <div className="max-w-6xl mx-auto px-6">
                        <div className="text-center mb-16">
                            <h2 className="text-4xl font-bold text-slate-900 mb-6">WordPress Development Services</h2>
                            <p className="text-xl text-slate-600 max-w-3xl mx-auto">
                                Comprehensive WordPress solutions tailored to your business needs
                            </p>
                        </div>
                        
                        <div className="grid md:grid-cols-2 gap-8">
                            {services.map((service, index) => (
                                <div key={index} className="bg-slate-50 rounded-2xl p-8 border border-slate-200 hover:shadow-lg transition-shadow">
                                    <div className="w-16 h-16 bg-slate-900 rounded-xl flex items-center justify-center text-white mb-6">
                                        {service.icon}
                                    </div>
                                    <h3 className="text-2xl font-bold text-slate-900 mb-4">{service.title}</h3>
                                    <p className="text-slate-600 mb-6 leading-relaxed">{service.description}</p>
                                    <div className="grid grid-cols-2 gap-3">
                                        {service.features.map((feature, featureIndex) => (
                                            <div key={featureIndex} className="flex items-center">
                                                <CheckCircle className="w-4 h-4 text-green-600 mr-2" />
                                                <span className="text-slate-700 text-sm">{feature}</span>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </section>

                {/* Features Section */}
                <section className="py-24 bg-slate-50">
                    <div className="max-w-6xl mx-auto px-6">
                        <div className="text-center mb-16">
                            <h2 className="text-4xl font-bold text-slate-900 mb-6">Why Choose Our WordPress Development?</h2>
                            <p className="text-xl text-slate-600 max-w-3xl mx-auto">
                                We deliver WordPress solutions that combine beautiful design with robust functionality
                            </p>
                        </div>
                        
                        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
                            {features.map((feature, index) => (
                                <div key={index} className="bg-white rounded-2xl p-8 border border-slate-200 hover:shadow-lg transition-shadow">
                                    <div className="w-16 h-16 bg-slate-900 rounded-xl flex items-center justify-center text-white mb-6">
                                        {feature.icon}
                                    </div>
                                    <h3 className="text-xl font-bold text-slate-900 mb-4">{feature.title}</h3>
                                    <p className="text-slate-600 leading-relaxed">{feature.description}</p>
                                </div>
                            ))}
                        </div>
                    </div>
                </section>

                {/* Solutions Section */}
                <section className="py-24 bg-white">
                    <div className="max-w-6xl mx-auto px-6">
                        <div className="text-center mb-16">
                            <h2 className="text-4xl font-bold text-slate-900 mb-6">WordPress Solutions We Deliver</h2>
                            <p className="text-xl text-slate-600 max-w-3xl mx-auto">
                                From simple business websites to complex e-commerce platforms
                            </p>
                        </div>
                        
                        <div className="grid md:grid-cols-2 gap-8">
                            {solutions.map((solution, index) => (
                                <div key={index} className="bg-slate-50 rounded-2xl p-8 border border-slate-200 hover:shadow-lg transition-shadow">
                                    <h3 className="text-2xl font-bold text-slate-900 mb-4">{solution.title}</h3>
                                    <p className="text-slate-600 mb-6 leading-relaxed">{solution.description}</p>
                                    <div className="space-y-3">
                                        {solution.features.map((feature, featureIndex) => (
                                            <div key={featureIndex} className="flex items-center">
                                                <CheckCircle className="w-4 h-4 text-green-600 mr-3" />
                                                <span className="text-slate-700">{feature}</span>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </section>

                {/* Technologies Section */}
                <section className="py-24 bg-slate-50">
                    <div className="max-w-6xl mx-auto px-6">
                        <div className="text-center mb-16">
                            <h2 className="text-4xl font-bold text-slate-900 mb-6">WordPress Technologies & Tools</h2>
                            <p className="text-xl text-slate-600 max-w-3xl mx-auto">
                                We use the latest WordPress technologies and industry-leading tools
                            </p>
                        </div>
                        
                        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
                            {technologies.map((tech, index) => (
                                <div key={index} className="bg-white rounded-2xl p-6 border border-slate-200">
                                    <h3 className="text-lg font-bold text-slate-900 mb-4">{tech.category}</h3>
                                    <div className="space-y-2">
                                        {tech.items.map((item, itemIndex) => (
                                            <div key={itemIndex} className="flex items-center">
                                                <div className="w-2 h-2 bg-slate-900 rounded-full mr-3"></div>
                                                <span className="text-slate-700 text-sm">{item}</span>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </section>

                {/* Process Section */}
                <section className="py-24 bg-white">
                    <div className="max-w-6xl mx-auto px-6">
                        <div className="text-center mb-16">
                            <h2 className="text-4xl font-bold text-slate-900 mb-6">Our WordPress Development Process</h2>
                            <p className="text-xl text-slate-600 max-w-3xl mx-auto">
                                A proven methodology that ensures successful WordPress project delivery
                            </p>
                        </div>
                        
                        <div className="space-y-8">
                            {process.map((step, index) => (
                                <div key={index} className="flex items-start">
                                    <div className="flex-shrink-0 w-16 h-16 bg-slate-900 rounded-xl flex items-center justify-center text-white font-bold text-lg mr-6">
                                        {step.step}
                                    </div>
                                    <div className="flex-1">
                                        <h3 className="text-2xl font-bold text-slate-900 mb-4">{step.title}</h3>
                                        <p className="text-lg text-slate-600 leading-relaxed">{step.description}</p>
                                    </div>
                                    {/* {index < process.length - 1 && (
                                        <ChevronRight className="w-6 h-6 text-slate-400 ml-6" />
                                    )} */}
                                </div>
                            ))}
                        </div>
                    </div>
                </section>

                {/* Benefits Section */}
                <section className="py-24 bg-slate-50">
                    <div className="max-w-6xl mx-auto px-6">
                        <div className="grid lg:grid-cols-2 gap-16 items-center">
                            <div>
                                <h2 className="text-4xl font-bold text-slate-900 mb-8">What You Get</h2>
                                <p className="text-xl text-slate-600 mb-10 leading-relaxed">
                                    Our WordPress development services provide comprehensive solutions that drive business 
                                    growth and deliver exceptional user experiences.
                                </p>
                                
                                <div className="space-y-4">
                                    {benefits.map((benefit, index) => (
                                        <div key={index} className="flex items-start">
                                            <CheckCircle className="w-6 h-6 text-green-600 mr-4 flex-shrink-0 mt-0.5" />
                                            <span className="text-slate-700 text-lg">{benefit}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                            
                            <div className="bg-white rounded-2xl p-8 border border-slate-200 shadow-lg">
                                <h3 className="text-2xl font-bold text-slate-900 mb-6">Industries We Serve</h3>
                                <div className="grid grid-cols-2 gap-4">
                                    {industries.map((industry, index) => (
                                        <div key={index} className="flex items-center p-3 bg-slate-50 rounded-lg">
                                            <div className="w-2 h-2 bg-slate-900 rounded-full mr-3"></div>
                                            <span className="text-slate-700 text-sm">{industry}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>
                </section>

                {/* Performance Stats */}
                <section className="py-24 bg-white">
                    <div className="max-w-6xl mx-auto px-6">
                        <div className="bg-slate-900 rounded-3xl p-12 text-white">
                            <div className="text-center mb-12">
                                <h2 className="text-4xl font-bold mb-6">WordPress Performance Excellence</h2>
                                <p className="text-xl text-slate-300 max-w-3xl mx-auto">
                                    Our WordPress websites consistently deliver exceptional performance and user satisfaction
                                </p>
                            </div>
                            
                            <div className="grid md:grid-cols-4 gap-8 text-center">
                                <div>
                                    <div className="text-4xl font-bold mb-2">95+</div>
                                    <div className="text-slate-300">PageSpeed Score</div>
                                </div>
                                <div>
                                    <div className="text-4xl font-bold mb-2">&lt;2s</div>
                                    <div className="text-slate-300">Load Time</div>
                                </div>
                                <div>
                                    <div className="text-4xl font-bold mb-2">A+</div>
                                    <div className="text-slate-300">Security Rating</div>
                                </div>
                                <div>
                                    <div className="text-4xl font-bold mb-2">99.9%</div>
                                    <div className="text-slate-300">Uptime</div>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>

                {/* CTA Section */}
                <CTA />
            </div>
        </Layout>
    );
};

export default WordPressDevelopmentPage;